import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, KeyRound, BookOpen } from "lucide-react";
import api, { formatApiError } from "@/utils/api";
import { Section } from "@/components/dashboard/DashboardPrimitives";

const inputCls = "px-3 py-2 text-sm rounded bg-[#0E1A3A] border border-slate-700 text-slate-100 focus:border-sky-400 outline-none";

function NewKeyBanner({ raw, onClose }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(raw); toast.success("API key copied"); }
    catch { toast.error("Couldn't copy. Please select the key and copy it manually."); }
  };
  return (
    <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 mb-4" data-testid="api-key-created-banner">
      <p className="text-sm font-semibold text-emerald-200 mb-1">Copy this key now. It will not be shown again.</p>
      <div className="flex items-center gap-2 mt-2">
        <code className="flex-1 break-all text-xs bg-[#0A1029] border border-slate-700 rounded px-3 py-2 text-slate-100" data-testid="api-key-created-value">{raw}</code>
        <button onClick={copy} className="px-3 py-2 rounded bg-emerald-500 hover:bg-emerald-400 text-white text-xs inline-flex items-center gap-1" data-testid="api-key-copy-btn">
          <Copy className="w-3.5 h-3.5" /> Copy
        </button>
        <button onClick={onClose} className="px-3 py-2 rounded border border-slate-600 text-slate-300 text-xs" data-testid="api-key-banner-close-btn">Done</button>
      </div>
    </div>
  );
}

function CreateKeyForm({ onCreated }) {
  const [form, setForm] = useState({ owner_email: "", name: "", billing_mode: "credits" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/api/admin/api-keys", form);
      onCreated(data.key);
      setForm({ owner_email: "", name: "", billing_mode: "credits" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr_0.8fr_auto] gap-2 mb-4" data-testid="api-key-create-form">
      <input required type="email" placeholder="Partner account email" value={form.owner_email} onChange={set("owner_email")} className={inputCls} data-testid="api-key-owner-email-input" />
      <input required placeholder="Label (e.g. Apollo HIS)" value={form.name} onChange={set("name")} className={inputCls} data-testid="api-key-name-input" />
      <select value={form.billing_mode} onChange={set("billing_mode")} className={inputCls} data-testid="api-key-billing-select">
        <option value="credits">Uses credit wallet</option>
        <option value="free">Free (approved)</option>
      </select>
      <button disabled={busy} className="px-4 py-2 text-sm rounded bg-sky-500 hover:bg-sky-400 text-white disabled:opacity-60 inline-flex items-center gap-1.5 justify-center" data-testid="api-key-create-btn">
        <KeyRound className="w-4 h-4" /> {busy ? "Creating…" : "Create key"}
      </button>
    </form>
  );
}

function KeyRow({ k, canRevoke, onRevoke }) {
  const revoked = !!k.revoked_at;
  return (
    <div className="p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`api-key-row-${k.key_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-slate-100">{k.name}</span>
          <code className="text-[11px] text-slate-400">{k.prefix}…</code>
          <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${k.billing_mode === "free" ? "bg-violet-500/20 text-violet-200" : "bg-sky-500/20 text-sky-200"}`}>
            {k.billing_mode === "free" ? "free" : "credits"}
          </span>
          {revoked && <span className="text-[10px] uppercase bg-rose-500/20 text-rose-300 px-1.5 py-0.5 rounded">revoked</span>}
        </div>
        <p className="text-xs text-slate-500 mt-0.5 truncate">
          {k.owner_name} · {k.owner_email} · {k.usage_count} calls · last used {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "never"}
        </p>
      </div>
      {canRevoke && !revoked && (
        <button onClick={() => onRevoke(k)} className="text-xs px-2.5 py-1.5 rounded border bg-rose-500/10 hover:bg-rose-500/20 text-rose-200 border-rose-500/30" data-testid={`api-key-revoke-${k.key_id}`}>
          Revoke
        </button>
      )}
    </div>
  );
}

export default function ApiKeysPanel({ isSuper }) {
  const [keys, setKeys] = useState([]);
  const [newKey, setNewKey] = useState(null);
  const load = () => api.get("/api/admin/api-keys").then(({ data }) => setKeys(data.keys)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Apps using it will stop working immediately.`)) return;
    try { await api.post(`/api/admin/api-keys/${k.key_id}/revoke`); toast.success("Key revoked"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Section
      title="Partner API keys"
      description="Keys let hospitals, schools and labs call the tots360 API from their own software."
      testid="admin-api-keys-section"
      right={<Link to="/developers" className="text-xs text-sky-300 hover:text-sky-200 inline-flex items-center gap-1" data-testid="api-keys-docs-link"><BookOpen className="w-3.5 h-3.5" /> API docs</Link>}
    >
      {isSuper && newKey && <NewKeyBanner raw={newKey} onClose={() => setNewKey(null)} />}
      {isSuper && <CreateKeyForm onCreated={(raw) => { setNewKey(raw); load(); }} />}
      <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 divide-y divide-slate-800/80" data-testid="api-keys-list">
        {keys.length === 0 && <p className="p-6 text-sm text-slate-400">No API keys issued yet.</p>}
        {keys.map((k) => <KeyRow key={k.key_id} k={k} canRevoke={isSuper} onRevoke={revoke} />)}
      </div>
    </Section>
  );
}
